// Profile Settings Page — refreshed for the Dashboard Refresh design system.
//
// Layout:
//   ┌─────────────────────────────────────────────────────┐
//   │ PageHeader                                          │
//   ├──────────────┬──────────────────────────────────────┤
//   │  Sub-sidebar │  Active section panel                │
//   │  (start-side │  (Card on the end side)              │
//   │   sticky)    │                                      │
//   └──────────────┴──────────────────────────────────────┘
// On mobile (< md): sub-sidebar collapses to a horizontal scrolling pill row.
// All physical direction classes are logical (start/end, ms/me, ps/pe) so the
// layout flips automatically under RTL.
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  HiOutlineUserCircle,
  HiOutlineKey,
  HiOutlineCog,
  HiOutlineCheck,
  HiOutlineArrowLeft,
  HiOutlineShieldCheck,
  HiOutlineBell,
  HiOutlineCreditCard,
  HiOutlineGlobeAlt,
  HiOutlineUserGroup,
  HiOutlineFolder,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineChatBubbleLeft,
  HiOutlineLink,
  HiOutlineRectangleStack,
  HiOutlineArchiveBoxArrowDown,
  HiOutlineInformationCircle,
} from 'react-icons/hi2'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService, { notificationPreferenceAPI } from '../../services/api'
import { TERMINOLOGY_TEMPLATES, ENTITY_KEYS, normalizeCustomTerminology } from '../../config/workspaceTerminology'
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui'
import DataExportTab from './DataExportTab'
import SubscriptionAndStorageTab from './SubscriptionAndStorageTab'

// Notification preference items — labels resolved at render time via t().
// `tone` maps to the new semantic tone system.
const NOTIF_PREF_ITEMS = [
  { key: 'notify_client',         labelKey: 'client',        descKey: 'clientDesc',        icon: HiOutlineUserGroup,      tone: 'info' },
  { key: 'notify_case',           labelKey: 'case',          descKey: 'caseDesc',          icon: HiOutlineFolder,         tone: 'brand' },
  { key: 'notify_session',        labelKey: 'session',       descKey: 'sessionDesc',       icon: HiOutlineCalendar,       tone: 'success' },
  { key: 'notify_document',       labelKey: 'document',      descKey: 'documentDesc',      icon: HiOutlineDocumentText,   tone: 'warning' },
  { key: 'notify_document_group', labelKey: 'documentGroup', descKey: 'documentGroupDesc', icon: HiOutlineRectangleStack, tone: 'info' },
  { key: 'notify_message',        labelKey: 'message',       descKey: 'messageDesc',       icon: HiOutlineChatBubbleLeft, tone: 'brand' },
  { key: 'notify_connection',     labelKey: 'connection',    descKey: 'connectionDesc',    icon: HiOutlineLink,           tone: 'brand' },
  { key: 'notify_system',         labelKey: 'system',        descKey: 'systemDesc',        icon: HiOutlineCog,            tone: 'neutral' },
]

// Sections rendered in the sub-sidebar. `ownerOnly` hides the section when
// viewing a switched (member) workspace — Drive + plan management is
// owner-only territory. `labelKey` / `descKey` resolve via t() at render.
const SECTIONS = [
  { id: 'profile',      labelKey: 'profile',      descKey: 'profileDesc',      icon: HiOutlineUserCircle,    tone: 'brand' },
  { id: 'workspace',    labelKey: 'workspace',    descKey: 'workspaceDesc',    icon: HiOutlineGlobeAlt,      tone: 'info' },
  { id: 'plan-storage', labelKey: 'planStorage',  descKey: 'planStorageDesc',  icon: HiOutlineCreditCard,    tone: 'brand',   ownerOnly: true },
  { id: 'data-export',  labelKey: 'dataExport',   descKey: 'dataExportDesc',   icon: HiOutlineArchiveBoxArrowDown, tone: 'info', ownerOnly: true },
  { id: 'security',     labelKey: 'security',     descKey: 'securityDesc',     icon: HiOutlineShieldCheck,   tone: 'danger' },
  { id: 'notifications',labelKey: 'notifications',descKey: 'notificationsDesc',icon: HiOutlineBell,          tone: 'warning' },
  { id: 'preferences',  labelKey: 'preferences',  descKey: 'preferencesDesc',  icon: HiOutlineCog,           tone: 'neutral' },
]

// Tailwind tone chip classes for the sidebar/section icon backgrounds.
const TONE_CHIP = {
  brand:   'bg-brand-soft   text-brand',
  info:    'bg-info-soft    text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger:  'bg-danger-soft  text-danger',
  neutral: 'bg-muted        text-ink-secondary',
}

const ProfileSettingsPage = () => {
  const { t, i18n } = useTranslation()
  // Language-fixed translators so the bilingual preview can render the English
  // sentence in English and the Arabic sentence in Arabic at the same time,
  // regardless of the page's current UI language.
  const tEn = i18n.getFixedT('en')
  const tAr = i18n.getFixedT('ar')
  const { isWorkspaceSwitched, ownerAccountId, switchedAccountName } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { workspaceType, updateTerminology, customTerminology } = useWorkspace()

  // Honor ?tab=<id> on initial mount (used by OAuthCallbackPage redirect).
  // Map the old `cloud-storage` and `subscription` query params to the new
  // unified `plan-storage` so deep links don't 404.
  const rawTab = searchParams.get('tab') || 'profile'
  const initialTab =
    rawTab === 'cloud-storage' || rawTab === 'subscription' ? 'plan-storage' : rawTab
  const [activeTab, setActiveTab] = useState(initialTab)

  // Sync activeTab back to URL so refreshes / share-links preserve it.
  useEffect(() => {
    const current = searchParams.get('tab')
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams)
      next.set('tab', activeTab)
      setSearchParams(next, { replace: true })
    }
  }, [activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState(null)
  const [notifLoading, setNotifLoading] = useState(false)
  const [notifSaving, setNotifSaving] = useState(null)

  // Workspace terminology state
  const [selectedTemplate, setSelectedTemplate] = useState(workspaceType || 'legal')
  const [editingTerminology, setEditingTerminology] = useState({})
  const [showCustomEditor, setShowCustomEditor] = useState(false)

  useEffect(() => {
    if (workspaceType) setSelectedTemplate(workspaceType)
    // Normalize any legacy (English-only flat) custom terminology into the
    // bilingual shape so the editor shows existing overrides in the EN column.
    if (customTerminology) setEditingTerminology(normalizeCustomTerminology(customTerminology))
  }, [workspaceType, customTerminology])

  const [profileData, setProfileData] = useState({
    name: '', email: '', phone: '', country: '', bio: ''
  })
  const [accountData, setAccountData] = useState({
    id: null, name: '', created_at: '', is_active: false
  })
  const [securityData, setSecurityData] = useState({
    current_password: '', new_password: '', confirm_password: ''
  })
  const [preferencesData, setPreferencesData] = useState({
    email_notifications: true, push_notifications: true, language: 'en', timezone: 'UTC'
  })

  useEffect(() => { fetchUserProfile() }, [])

  const fetchUserProfile = async () => {
    try {
      setLoading(true)
      const response = await apiService.get('/user/profile/')
      if (response.success) {
        const profile = response.data.data || response.data
        setProfileData({
          name: profile.name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          country: profile.country || '',
          bio: profile.bio || '',
        })
        if (profile.account) {
          setAccountData({
            id: profile.account.id || null,
            name: profile.account.name || '',
            created_at: profile.account.created_at || '',
            is_active: profile.account.is_active || false,
          })
        }
      } else {
        showError(response.error?.message || t('profileSettings.profile.loadFailed'))
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
      showError(t('profileSettings.profile.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'notifications') fetchNotifPrefs()
  }, [activeTab, isWorkspaceSwitched, ownerAccountId])

  const fetchNotifPrefs = async () => {
    try {
      setNotifLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await notificationPreferenceAPI.getPreferences(params)
      if (response.success) setNotifPrefs(response.data?.data || response.data)
    } catch (e) {
      console.error('Error fetching notification preferences:', e)
    } finally {
      setNotifLoading(false)
    }
  }

  const handleNotifToggle = async (key) => {
    if (!notifPrefs) return
    const newValue = !notifPrefs[key]
    const old = { ...notifPrefs }
    setNotifPrefs(prev => ({ ...prev, [key]: newValue }))
    setNotifSaving(key)
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await notificationPreferenceAPI.updatePreferences({ [key]: newValue }, params)
      if (response.success) {
        setNotifPrefs(response.data?.data || response.data)
        const item = NOTIF_PREF_ITEMS.find(p => p.key === key)
        const label = item ? t(`settings.notifications.${item.labelKey}`) : key
        showSuccess(t(newValue ? 'profileSettings.notifications.enabled' : 'profileSettings.notifications.disabled', { label }))
      } else {
        setNotifPrefs(old)
        showError(t('profileSettings.notifications.updateFailed'))
      }
    } catch (e) {
      setNotifPrefs(old)
      showError(t('profileSettings.notifications.updateFailed'))
    } finally {
      setNotifSaving(null)
    }
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      const response = await apiService.put('/user/profile/', profileData)
      if (response.success) showSuccess(t('profileSettings.profile.saveSuccess'))
      else showError(response.error?.message || t('profileSettings.profile.saveFailed'))
    } catch (error) {
      console.error('Error updating profile:', error)
      showError(t('profileSettings.profile.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleSecuritySubmit = async (e) => {
    e.preventDefault()
    if (securityData.new_password !== securityData.confirm_password) {
      showError(t('profileSettings.security.mismatch')); return
    }
    if (securityData.new_password.length < 8) {
      showError(t('profileSettings.security.tooShort')); return
    }
    try {
      setSaving(true)
      const response = await apiService.put('/user/change-password/', {
        current_password: securityData.current_password,
        new_password: securityData.new_password,
      })
      if (response.success) {
        showSuccess(t('profileSettings.security.updateSuccess'))
        setSecurityData({ current_password: '', new_password: '', confirm_password: '' })
      } else {
        showError(response.error?.message || t('profileSettings.security.updateFailed'))
      }
    } catch (error) {
      console.error('Error updating password:', error)
      showError(t('profileSettings.security.updateFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      const response = await apiService.put('/user/preferences/', preferencesData)
      if (response.success) showSuccess(t('profileSettings.preferences.saveSuccess'))
      else showError(response.error?.message || t('profileSettings.preferences.saveFailed'))
    } catch (error) {
      console.error('Error updating preferences:', error)
      showError(t('profileSettings.preferences.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleWorkspaceTypeChange = async () => {
    try {
      setSaving(true)
      const cleanedTerminology = {
        ...editingTerminology,
        entities: editingTerminology.entities || {},
      }
      const result = await updateTerminology(selectedTemplate, cleanedTerminology)
      if (result.success) {
        showSuccess(t('profileSettings.workspace.saveSuccess'))
        setShowCustomEditor(false)
        setTimeout(() => window.location.reload(), 1500)
      } else {
        showError(result.error || t('profileSettings.workspace.saveFailed'))
      }
    } catch (error) {
      console.error('Error updating workspace:', error)
      showError(t('profileSettings.workspace.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  // Bilingual custom-label editing.  `lang` is 'en' | 'ar', `form` is
  // 'singular' | 'plural'.  Stored shape:
  //   editingTerminology.entities[entity][lang][form] = 'value'
  const handleCustomTermChange = (entity, lang, form, value) => {
    setEditingTerminology(prev => {
      const v = value.trim()
      const entities = { ...(prev.entities || {}) }
      const entityNode = { ...(entities[entity] || {}) }
      const langNode = { ...(entityNode[lang] || {}) }

      if (v) {
        langNode[form] = v
      } else {
        delete langNode[form]
      }

      if (Object.keys(langNode).length) {
        entityNode[lang] = langNode
      } else {
        delete entityNode[lang]
      }

      if (Object.keys(entityNode).length) {
        entities[entity] = entityNode
      } else {
        delete entities[entity]
      }

      return { ...prev, entities }
    })
  }

  const getCustomValue = (entity, lang, form) =>
    editingTerminology?.entities?.[entity]?.[lang]?.[form] || ''

  // Resolve the effective preview label for an entity (custom override falls
  // back to the selected template's value) in a given language.
  const previewLabel = (entity, lang, form) =>
    getCustomValue(entity, lang, form) ||
    TERMINOLOGY_TEMPLATES[selectedTemplate]?.entities?.[entity]?.[lang]?.[form] ||
    ''

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Spinner size="md" />
          <span className="text-sm text-ink-muted">{t('profileSettings.loading')}</span>
        </div>
      </div>
    )
  }

  // Filter sections based on whether user is the workspace owner.
  const visibleSections = SECTIONS.filter(s => !(s.ownerOnly && isWorkspaceSwitched))
  const activeSection = visibleSections.find(s => s.id === activeTab) || visibleSections[0]

  return (
    <div className="min-h-screen bg-canvas">
      {/* Slim back-bar above the page header. Uses logical -ms- spacing so the
          back arrow hugs the start edge in both LTR and RTL. */}
      <div className="bg-surface border-b border-border px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14 max-w-6xl mx-auto">
          <IconButton
            icon={HiOutlineArrowLeft}
            variant="ghost"
            size="md"
            onClick={() => navigate('/dashboard')}
            aria-label={t('profileSettings.backToDashboard')}
            className="-ms-2 rtl:rotate-180"
          />
          <span className="ms-2 text-xs text-ink-muted">{t('profileSettings.backToDashboard')}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <PageHeader
          icon={HiOutlineCog}
          iconTone="brand"
          title={t('profileSettings.title')}
          subtitle={t('profileSettings.subtitle')}
        />

        {/* Mobile pill nav (visible < md) */}
        <div className="md:hidden mb-4 -mx-1 overflow-x-auto">
          <div className="flex gap-2 px-1 pb-1">
            {visibleSections.map(s => {
              const Icon = s.icon
              const isActive = activeTab === s.id
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveTab(s.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap border transition-colors ${
                    isActive
                      ? 'bg-brand-soft text-brand border-brand/30'
                      : 'bg-surface text-ink-secondary border-border hover:bg-muted'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t(`profileSettings.sections.${s.labelKey}`)}
                </button>
              )
            })}
          </div>
        </div>

        <div className="md:grid md:grid-cols-[240px_1fr] md:gap-6">
          {/* Sub-sidebar (visible >= md). Anchored to the start side via grid
              order. Uses `Card padding="none"` so it picks up surface + border
              + shadow tokens automatically. */}
          <aside className="hidden md:block">
            <Card
              as="nav"
              padding="none"
              className="p-2 sticky top-5"
              aria-label={t('profileSettings.sectionsNavLabel')}
            >
              {visibleSections.map(s => {
                const Icon = s.icon
                const isActive = activeTab === s.id
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveTab(s.id)}
                    className={`w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-start transition-colors mb-0.5 last:mb-0 ${
                      isActive
                        ? 'bg-brand-soft text-brand'
                        : 'text-ink-secondary hover:bg-muted'
                    }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                        isActive ? 'bg-surface/70' : TONE_CHIP[s.tone] || TONE_CHIP.neutral
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-semibold leading-tight">
                        {t(`profileSettings.sections.${s.labelKey}`)}
                      </span>
                      <span
                        className={`block text-[11px] leading-tight mt-0.5 truncate ${
                          isActive ? 'opacity-80' : 'text-ink-muted'
                        }`}
                      >
                        {t(`profileSettings.sections.${s.descKey}`)}
                      </span>
                    </span>
                  </button>
                )
              })}
            </Card>
          </aside>

          {/* Active section content */}
          <main className="min-w-0">
            {/* Section header (matches sidebar selection) */}
            {activeSection && (
              <div className="mb-3 flex items-center gap-2.5">
                {(() => {
                  const Icon = activeSection.icon
                  const chip = TONE_CHIP[activeSection.tone] || TONE_CHIP.neutral
                  return (
                    <>
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${chip}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <div>
                        <h2 className="text-sm font-semibold text-ink-primary leading-tight">
                          {t(`profileSettings.sections.${activeSection.labelKey}`)}
                        </h2>
                        <p className="text-[11px] text-ink-muted leading-tight">
                          {t(`profileSettings.sections.${activeSection.descKey}`)}
                        </p>
                      </div>
                    </>
                  )
                })()}
              </div>
            )}

            <Card padding="md">
              {/* Profile */}
              {activeTab === 'profile' && (
                <form onSubmit={handleProfileSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label={t('profileSettings.profile.fullName')} required>
                      <Input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        required
                      />
                    </FormField>
                    <FormField label={t('profileSettings.profile.email')} required>
                      <Input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        required
                      />
                    </FormField>
                    <FormField label={t('profileSettings.profile.phone')}>
                      <Input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      />
                    </FormField>
                    <FormField label={t('profileSettings.profile.country')}>
                      <Select
                        value={profileData.country}
                        onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                      >
                        <option value="">{t('profileSettings.profile.selectCountry')}</option>
                        <option value="US">{t('profileSettings.profile.countries.us')}</option>
                        <option value="CA">{t('profileSettings.profile.countries.ca')}</option>
                        <option value="GB">{t('profileSettings.profile.countries.gb')}</option>
                        <option value="AU">{t('profileSettings.profile.countries.au')}</option>
                        <option value="DE">{t('profileSettings.profile.countries.de')}</option>
                        <option value="FR">{t('profileSettings.profile.countries.fr')}</option>
                      </Select>
                    </FormField>
                  </div>

                  <FormField label={t('profileSettings.profile.bio')}>
                    <Textarea
                      value={profileData.bio}
                      onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                      rows={3}
                      placeholder={t('profileSettings.profile.bioPlaceholder')}
                    />
                  </FormField>

                  {/* Account info */}
                  <div className="border-t border-border-subtle pt-4">
                    <h3 className="text-xs font-semibold text-ink-primary mb-3 uppercase tracking-wide">
                      {t('profileSettings.profile.accountInfo')}
                    </h3>
                    {accountData.id ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Readonly
                          label={t('profileSettings.profile.accountName')}
                          value={accountData.name || t('profileSettings.profile.noAccountName')}
                        />
                        <div>
                          <p className="text-xs font-medium text-ink-secondary mb-1.5">
                            {t('profileSettings.profile.accountStatus')}
                          </p>
                          <div className="px-3 py-2 bg-muted border border-border rounded-lg">
                            <Badge tone={accountData.is_active ? 'success' : 'danger'} size="sm">
                              {accountData.is_active ? t('common.active') : t('common.inactive')}
                            </Badge>
                          </div>
                        </div>
                        <Readonly
                          label={t('profileSettings.profile.accountId')}
                          value={`#${accountData.id}`}
                          mono
                        />
                        <Readonly
                          label={t('profileSettings.profile.createdLabel')}
                          value={
                            accountData.created_at
                              ? new Date(accountData.created_at).toLocaleDateString()
                              : t('profileSettings.profile.unknownDate')
                          }
                        />
                      </div>
                    ) : (
                      <p className="text-xs text-ink-muted">{t('profileSettings.profile.noAccount')}</p>
                    )}
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      size="sm"
                      loading={saving}
                      leadingIcon={!saving ? HiOutlineCheck : undefined}
                    >
                      {saving ? t('common.saving') : t('common.saveChanges')}
                    </Button>
                  </div>
                </form>
              )}

              {/* Plan & Storage (combined) */}
              {activeTab === 'plan-storage' && (
                <SubscriptionAndStorageTab isOwner={!isWorkspaceSwitched} />
              )}

              {/* Data Export */}
              {activeTab === 'data-export' && (
                <DataExportTab isOwner={!isWorkspaceSwitched} />
              )}

              {/* Security */}
              {activeTab === 'security' && (
                <form onSubmit={handleSecuritySubmit} className="space-y-4">
                  <div className="space-y-3">
                    <FormField label={t('profileSettings.security.currentPassword')} required>
                      <Input
                        type="password"
                        value={securityData.current_password}
                        onChange={(e) => setSecurityData({ ...securityData, current_password: e.target.value })}
                        required
                      />
                    </FormField>
                    <FormField
                      label={t('profileSettings.security.newPassword')}
                      required
                      help={t('profileSettings.security.minChars')}
                    >
                      <Input
                        type="password"
                        value={securityData.new_password}
                        onChange={(e) => setSecurityData({ ...securityData, new_password: e.target.value })}
                        required
                      />
                    </FormField>
                    <FormField label={t('profileSettings.security.confirmPassword')} required>
                      <Input
                        type="password"
                        value={securityData.confirm_password}
                        onChange={(e) => setSecurityData({ ...securityData, confirm_password: e.target.value })}
                        required
                      />
                    </FormField>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      size="sm"
                      loading={saving}
                      leadingIcon={!saving ? HiOutlineKey : undefined}
                    >
                      {saving ? t('profileSettings.security.updating') : t('profileSettings.security.updateButton')}
                    </Button>
                  </div>
                </form>
              )}

              {/* Workspace terminology */}
              {activeTab === 'workspace' && (
                <div className="space-y-4">
                  <p className="text-xs text-ink-secondary">{t('profileSettings.workspace.intro')}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(TERMINOLOGY_TEMPLATES).map(([key, template]) => {
                      const isSelected = selectedTemplate === key
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSelectedTemplate(key)}
                          className={`text-start p-3 rounded-lg border-2 transition-all ${
                            isSelected
                              ? 'border-brand bg-brand-soft'
                              : 'border-border hover:border-brand/40 bg-surface'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-ink-primary mb-0.5">
                                {template.name} <span className="text-ink-muted font-normal">· {template.nameAr}</span>
                              </h3>
                              <p className="text-xs text-ink-secondary mb-2">{template.description}</p>
                              {/* Bilingual quick preview: English line + Arabic line */}
                              <div className="text-[11px] text-ink-muted space-y-0.5">
                                <div dir="ltr">{template.entities.case.en.plural} · {template.entities.client.en.plural} · {template.entities.session.en.plural}</div>
                                <div dir="rtl">{template.entities.case.ar.plural} · {template.entities.client.ar.plural} · {template.entities.session.ar.plural}</div>
                              </div>
                            </div>
                            {isSelected && (
                              <HiOutlineCheck className="w-5 h-5 text-brand flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>

                  <div className="border-t border-border-subtle pt-3 flex items-center justify-between">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setShowCustomEditor(!showCustomEditor)}
                    >
                      {showCustomEditor
                        ? t('profileSettings.workspace.hideCustomEditor')
                        : t('profileSettings.workspace.showCustomEditor')}
                    </Button>
                    {Object.keys(editingTerminology?.entities || {}).length > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setEditingTerminology({})}
                        className="!text-danger hover:!text-danger/80"
                      >
                        {t('profileSettings.workspace.clearCustom')}
                      </Button>
                    )}
                  </div>

                  {showCustomEditor && (
                    <div className="bg-muted rounded-lg p-4 space-y-3 border border-border">
                      <div>
                        <h3 className="text-xs font-semibold text-ink-primary">
                          {t('profileSettings.workspace.customLabels')}
                        </h3>
                        <p className="text-[11px] text-ink-secondary mt-0.5">
                          {t('profileSettings.workspace.customLabelsHint')}
                        </p>
                      </div>
                      <Banner
                        tone="info"
                        icon={HiOutlineInformationCircle}
                        title={t('profileSettings.workspace.tipTitle')}
                      >
                        {t('profileSettings.workspace.tipBody')}
                      </Banner>
                      <div className="grid grid-cols-1 gap-2.5">
                        {Object.entries(TERMINOLOGY_TEMPLATES[selectedTemplate].entities).map(([entity, terms]) => (
                          <div key={entity} className="bg-surface rounded-md p-3 border border-border">
                            <h4 className="text-xs font-semibold text-ink-primary mb-2 capitalize">{entity}</h4>
                            {/* English column (left) · Arabic column (right) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* English */}
                              <div className="md:border-e md:border-border-subtle md:pe-3">
                                <p className="text-[11px] font-semibold text-ink-secondary uppercase tracking-wide mb-1.5">
                                  {t('profileSettings.workspace.englishColumn')}
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  <FormField
                                    label={t('profileSettings.workspace.singular')}
                                    help={`${t('profileSettings.workspace.defaultPrefix')}: ${terms.en.singular}`}
                                  >
                                    <Input
                                      size="sm" type="text" dir="ltr"
                                      placeholder={terms.en.singular}
                                      value={getCustomValue(entity, 'en', 'singular')}
                                      onChange={(e) => handleCustomTermChange(entity, 'en', 'singular', e.target.value)}
                                    />
                                  </FormField>
                                  <FormField
                                    label={t('profileSettings.workspace.plural')}
                                    help={`${t('profileSettings.workspace.defaultPrefix')}: ${terms.en.plural}`}
                                  >
                                    <Input
                                      size="sm" type="text" dir="ltr"
                                      placeholder={terms.en.plural}
                                      value={getCustomValue(entity, 'en', 'plural')}
                                      onChange={(e) => handleCustomTermChange(entity, 'en', 'plural', e.target.value)}
                                    />
                                  </FormField>
                                </div>
                              </div>
                              {/* Arabic */}
                              <div>
                                <p className="text-[11px] font-semibold text-ink-secondary uppercase tracking-wide mb-1.5">
                                  {t('profileSettings.workspace.arabicColumn')}
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  <FormField
                                    label={t('profileSettings.workspace.singular')}
                                    help={`${t('profileSettings.workspace.defaultPrefix')}: ${terms.ar.singular}`}
                                  >
                                    <Input
                                      size="sm" type="text" dir="rtl"
                                      placeholder={terms.ar.singular}
                                      value={getCustomValue(entity, 'ar', 'singular')}
                                      onChange={(e) => handleCustomTermChange(entity, 'ar', 'singular', e.target.value)}
                                    />
                                  </FormField>
                                  <FormField
                                    label={t('profileSettings.workspace.plural')}
                                    help={`${t('profileSettings.workspace.defaultPrefix')}: ${terms.ar.plural}`}
                                  >
                                    <Input
                                      size="sm" type="text" dir="rtl"
                                      placeholder={terms.ar.plural}
                                      value={getCustomValue(entity, 'ar', 'plural')}
                                      onChange={(e) => handleCustomTermChange(entity, 'ar', 'plural', e.target.value)}
                                    />
                                  </FormField>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Banner tone="info" title={t('profileSettings.workspace.previewTitle')}>
                    <p className="mb-2">{t('profileSettings.workspace.previewIntro')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* English preview */}
                      <div className="md:border-e md:border-border-subtle md:pe-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1 opacity-80">
                          {t('profileSettings.workspace.englishColumn')}
                        </p>
                        <ul className="space-y-0.5 list-disc list-inside ms-1" dir="ltr">
                          <li>{tEn('profileSettings.workspace.previewView', { value: previewLabel('case', 'en', 'plural') })}</li>
                          <li>{tEn('profileSettings.workspace.previewAdd', { value: previewLabel('client', 'en', 'singular') })}</li>
                          <li>{tEn('profileSettings.workspace.previewSchedule', { value: previewLabel('session', 'en', 'singular') })}</li>
                          <li>{tEn('profileSettings.workspace.previewUpload', { value: previewLabel('document', 'en', 'singular') })}</li>
                        </ul>
                      </div>
                      {/* Arabic preview */}
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1 opacity-80">
                          {t('profileSettings.workspace.arabicColumn')}
                        </p>
                        <ul className="space-y-0.5 list-disc list-inside ms-1" dir="rtl">
                          <li>{tAr('profileSettings.workspace.previewView', { value: previewLabel('case', 'ar', 'plural') })}</li>
                          <li>{tAr('profileSettings.workspace.previewAdd', { value: previewLabel('client', 'ar', 'singular') })}</li>
                          <li>{tAr('profileSettings.workspace.previewSchedule', { value: previewLabel('session', 'ar', 'singular') })}</li>
                          <li>{tAr('profileSettings.workspace.previewUpload', { value: previewLabel('document', 'ar', 'singular') })}</li>
                        </ul>
                      </div>
                    </div>
                  </Banner>

                  <div className="flex items-center justify-between pt-3 border-t border-border-subtle gap-3 flex-wrap">
                    <div className="text-xs">
                      {selectedTemplate !== workspaceType && (
                        <p className="text-warning font-medium">
                          {t('profileSettings.workspace.templateChanged', {
                            from: TERMINOLOGY_TEMPLATES[workspaceType]?.name,
                            to: TERMINOLOGY_TEMPLATES[selectedTemplate]?.name,
                          })}
                        </p>
                      )}
                      {Object.keys(editingTerminology?.entities || {}).length > 0 && (
                        <p className="text-brand font-medium">
                          {t('profileSettings.workspace.customCount', {
                            count: Object.keys(editingTerminology?.entities || {}).length,
                          })}
                        </p>
                      )}
                      {selectedTemplate === workspaceType && Object.keys(editingTerminology?.entities || {}).length === 0 && (
                        <p className="text-ink-muted">{t('profileSettings.workspace.noChanges')}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      loading={saving}
                      disabled={saving || (selectedTemplate === workspaceType && Object.keys(editingTerminology?.entities || {}).length === 0)}
                      leadingIcon={!saving ? HiOutlineCheck : undefined}
                      onClick={handleWorkspaceTypeChange}
                    >
                      {saving ? t('common.saving') : t('common.saveChanges')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Notifications */}
              {activeTab === 'notifications' && (
                <div className="space-y-4">
                  <Banner
                    tone={isWorkspaceSwitched ? 'info' : 'brand'}
                    icon={HiOutlineBell}
                    title={
                      isWorkspaceSwitched
                        ? t('profileSettings.notifications.switchedTitle', {
                            name: switchedAccountName || t('profileSettings.notifications.switchedWorkspaceFallback'),
                          })
                        : t('profileSettings.notifications.ownTitle')
                    }
                  >
                    {isWorkspaceSwitched
                      ? t('profileSettings.notifications.switchedHint')
                      : t('profileSettings.notifications.ownHint')}
                  </Banner>

                  <div>
                    <h3 className="text-xs font-semibold text-ink-primary uppercase tracking-wide">
                      {t('profileSettings.notifications.categoriesTitle')}
                    </h3>
                    <p className="text-[11px] text-ink-muted mt-0.5">
                      {t('profileSettings.notifications.categoriesHint')}
                    </p>
                  </div>

                  {notifLoading ? (
                    <div className="space-y-2">
                      {[...Array(8)].map((_, i) => (
                        <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : notifPrefs ? (
                    <div className="space-y-2">
                      {NOTIF_PREF_ITEMS.map((item) => {
                        const Icon = item.icon
                        const enabled = notifPrefs[item.key] ?? true
                        const isSaving = notifSaving === item.key
                        const label = t(`settings.notifications.${item.labelKey}`)
                        const desc = t(`settings.notifications.${item.descKey}`)
                        return (
                          <div
                            key={item.key}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                              enabled
                                ? 'border-border bg-surface'
                                : 'border-border-subtle bg-muted opacity-70'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${TONE_CHIP[item.tone] || TONE_CHIP.neutral}`}>
                                <Icon className="w-4 h-4" />
                              </span>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-ink-primary leading-tight">{label}</p>
                                <p className="text-[11px] text-ink-muted leading-tight">{desc}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleNotifToggle(item.key)}
                              disabled={isSaving}
                              role="switch"
                              aria-checked={enabled}
                              aria-label={label}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
                                enabled ? 'bg-brand' : 'bg-border-strong'
                              } ${isSaving ? 'opacity-50' : ''}`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                                  enabled ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5 rtl:-translate-x-0.5'
                                }`}
                              />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <EmptyState
                      icon={HiOutlineBell}
                      title={t('profileSettings.notifications.loadFailed')}
                      compact
                    />
                  )}

                  <div className="p-3 bg-muted rounded-lg text-[11px] text-ink-secondary border border-border-subtle">
                    {t('profileSettings.notifications.footnote')}
                  </div>
                </div>
              )}

              {/* Preferences */}
              {activeTab === 'preferences' && (
                <form onSubmit={handlePreferencesSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label={t('profileSettings.preferences.language')}>
                      <Select
                        value={preferencesData.language}
                        onChange={(e) => setPreferencesData({ ...preferencesData, language: e.target.value })}
                      >
                        <option value="en">{t('profileSettings.preferences.languages.en')}</option>
                        <option value="es">{t('profileSettings.preferences.languages.es')}</option>
                        <option value="fr">{t('profileSettings.preferences.languages.fr')}</option>
                        <option value="de">{t('profileSettings.preferences.languages.de')}</option>
                      </Select>
                    </FormField>
                    <FormField label={t('profileSettings.preferences.timezone')}>
                      <Select
                        value={preferencesData.timezone}
                        onChange={(e) => setPreferencesData({ ...preferencesData, timezone: e.target.value })}
                      >
                        <option value="UTC">{t('profileSettings.preferences.timezones.utc')}</option>
                        <option value="EST">{t('profileSettings.preferences.timezones.est')}</option>
                        <option value="PST">{t('profileSettings.preferences.timezones.pst')}</option>
                        <option value="GMT">{t('profileSettings.preferences.timezones.gmt')}</option>
                      </Select>
                    </FormField>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      size="sm"
                      loading={saving}
                      leadingIcon={!saving ? HiOutlineCheck : undefined}
                    >
                      {saving ? t('common.saving') : t('profileSettings.preferences.saveButton')}
                    </Button>
                  </div>
                </form>
              )}
            </Card>
          </main>
        </div>
      </div>
    </div>
  )
}

// Small helper for read-only key/value fields inside the Profile section.
// Uses semantic tokens so it dark-mode-flips correctly.
const Readonly = ({ label, value, mono = false }) => (
  <div>
    <p className="text-xs font-medium text-ink-secondary mb-1.5">{label}</p>
    <div
      className={`px-3 py-2 bg-muted border border-border rounded-lg text-xs text-ink-primary ${
        mono ? 'font-mono' : ''
      }`}
    >
      {value}
    </div>
  </div>
)

export default ProfileSettingsPage
